import { AddButton, PageSpinner } from '@td-client-frontend/common';
import React, { Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import Modal from '../../../components/Modal/NewModal';
import WithPermission from '../../../components/WithPermission';
import { useAuth } from '../../../context/AuthContext';
import ManageMember from '../ManageMember';
import { MemberSearch, MemberSearchResult } from './MemberSearch';
import MembersTable from './MembersTable';
import { ExclamationCircle } from '@td-client-frontend/icons';
import { format } from 'date-fns';
import { MemberSearchContext, MemberSearchContextProvider } from '../../../context/MemberSearchContext';
import { Permissions } from '../../../@types/Permissions';
import { MemberContextProvider, useMemberContext } from '../../../context/MemberContext';
import { MEMBER_QUERY_KEY_PREFIX } from '../../../hooks/useMemberDetail';
import { useAxios } from '../../../context/AxiosContext';
import { toast } from 'react-toastify';
import { defaultPageNumber } from '../utils';

const MEMBERS_API_PATH: string = '/members';

function ManageMembers() {
  const { checkPerm } = useAuth();
  const [searchFilter, setSearchFilter] = useState<{} | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [memberData, setMemberData] = useState(undefined);
  const queryClient = useQueryClient();
  const {
    isViewMode,
    setIsViewMode,
    handleClear,
    setActiveTab,
    setDependentId,
    itemsPerPage,
    currentPage,
    setCurrentPage
  } = useMemberContext();

  // Manage Member modal
  const [showManageMemberModal, setShowManageMemberModal] = useState<boolean>(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(process.env.REACT_APP_MEMBER_POPUP === 'Y');
  const { authAxios } = useAxios();
  const { setTooManyRecords } = useContext(MemberSearchContext);

  const fetchMembers = useCallback(
    async ({ search, skip, limit }) => {
      try {
        const response = await authAxios.get(MEMBERS_API_PATH, {
          params: {
            ...search,
            skip,
            limit
          }
        });
        setTooManyRecords(false);
        return response.data;
      } catch (err) {
        const { response, message } = err as any;
        if (response.status === 422) {
          setTooManyRecords(true);
        }
        toast.warning(response.data.message ?? message, {
          position: 'top-center',
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: true
        });
      }
    },
    [authAxios, setTooManyRecords]
  );
  const loadData = useCallback(async () => {
    setIsLoading(true);
    if (searchFilter) {
      const result = await fetchMembers({
        search: searchFilter,
        skip: itemsPerPage.value * currentPage - itemsPerPage.value,
        limit: itemsPerPage.value
      });
      setMemberData(result);
    }
    setIsLoading(false);
  }, [setIsLoading, setMemberData, fetchMembers, searchFilter, currentPage, itemsPerPage]);

  const toggleManageMemberModal = useCallback(
    async (result?: any) => {
      handleClear(); // resets modal tab, create flag, selected dependent id
      setSelectedMemberId(null);
      setShowManageMemberModal(!showManageMemberModal);
      if (showManageMemberModal) setIsViewMode(false);

      // closing the modal yields an object which is truthy, when modal is not open result is falsy
      if (result && typeof result === 'boolean') {
        queryClient.removeQueries([MEMBER_QUERY_KEY_PREFIX, selectedMemberId]); // we are done with member. hard reload on next iteration

        await loadData();
      }
    },
    [handleClear, showManageMemberModal, setIsViewMode, queryClient, selectedMemberId, loadData]
  );

  useEffect(() => {
    loadData();
    // should cause a reload whenever the search filter changes, or the page, or the items per page
  }, [searchFilter, currentPage, itemsPerPage, loadData]);

  const isNewMemberFlow = selectedMemberId === null;
  const readOnly = (checkPerm(Permissions.MemberRead) && !checkPerm(Permissions.MemberWrite)) || isViewMode;
  const headerLabel = isNewMemberFlow ? 'Add Primary Member' : readOnly ? 'Member Profile' : 'Edit Member Profile';

  const handleSearchChange = ({ firstName, lastName, dob, group }: MemberSearchResult) => {
    const urlSearchParams: Record<string, string> = {};
    if (firstName && firstName.length >= 2) {
      urlSearchParams['firstName'] = firstName;
    }

    if (dob && dob !== '') {
      urlSearchParams['dob'] = format(new Date(dob), 'yyyy-MM-dd');
    }

    if (lastName && lastName.length >= 2) {
      urlSearchParams['lastName'] = lastName;
    }

    if (group) {
      urlSearchParams['group'] = group.toString();
    }
    // if user changes filter, then send them back to page 1, and re-query
    setCurrentPage(defaultPageNumber); // doesn't this cause a re-render as soon as the page number changes? if it changes, would it reach the filter change? would a user on page 2 that changes their query result in 2 fetches?
    setSearchFilter(urlSearchParams);
  };

  const HeaderBar = () => {
    return (
      <>
        <div className="flex items-center justify-between w-full">
          <h1 className="text-4xl font-effra">Manage Members</h1>
          <WithPermission requiredPerms={[Permissions.MemberCreate]}>
            <AddButton onClick={toggleManageMemberModal} label="Add primary member" />
          </WithPermission>
        </div>
        <h2 className="text-xl font-effra mt-8 mb-3 flex flex-row items-center justify-between">
          Primary member search. <ExclamationCircle className="mx-1" /> Add date of birth to search criteria if member
          is not found.
        </h2>
      </>
    );
  };
  const Redirector = () => (
    <div className="p-4 justify-between">
      <p className="mb-4">
        Please resume managing your member eligibility @<Linker />
        until you are redirected back to this page.
      </p>
      <p className="mb-4">
        Prior to logging into the site <Linker />
        please reference the email communication you recently received related to the refreshed Teladoc Health Client
        Portal:
      </p>
      <ul className="list-disc pl-10 mb-4 space-y-3 ">
        <li>
          Username and Password Unchanged: Login <Linker /> with your original username and password
        </li>
        <li>
          New Username and Same Password: Login <Linker /> with new username and your original password
        </li>
        <li>
          Username and Password Changed: Login <Linker /> utilizing the credentials utilized prior to receiving the
          communication
        </li>
      </ul>
      <p className="mb-4">
        Once you are redirected back to this page, please login referencing the instructions located in the email
        communication recently received from Teladoc Health. This communication was related to the refreshed Teladoc
        Health Client Portal.
      </p>
      <p className="mb-4">
        Contact Teladoc Client Services - <Linker /> with questions or if you have trouble logging into your account.
      </p>
    </div>
  );

  const Linker = () => (
    <a target="_blank" href="https://client.teladoc.com/signin" rel="noreferrer">
      https://client.teladoc.com/signin
    </a>
  );

  return (
    <div className="flex flex-col items-center justify-between w-full px-6 py-4 pt-10 mx-auto bg-white sm:items-baseline">
      <Modal
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        header="IMPORTANT MESSAGE"
        actions={[
          {
            title: 'Close',
            onClick: () => setShowWarningModal(false),
            autoClose: true
          }
        ]}
        size="medium"
      >
        <Redirector />;
      </Modal>
      <Modal
        isOpen={showManageMemberModal}
        onClose={toggleManageMemberModal} // one hitch of this is that modal close sends back a massive object with functions, this causes a re-fetch
        contentLabel={`${headerLabel} Modal`}
        title={`${headerLabel} Modal`}
        header={headerLabel}
        size="large"
      >
        <ManageMember id={selectedMemberId} fetchMembers={fetchMembers} onRequestClose={toggleManageMemberModal} />;
      </Modal>

      <HeaderBar />
      <MemberSearchContextProvider>
        <MemberSearch onChange={handleSearchChange} />
        <MembersTable
          isLoading={isLoading}
          reFetchMembers={loadData}
          memberData={memberData}
          searchFilter={searchFilter}
          toggleManageMemberModal={toggleManageMemberModal}
          setSelectedMemberId={setSelectedMemberId}
          setDependentId={setDependentId}
          setActiveTab={setActiveTab}
        />
      </MemberSearchContextProvider>
    </div>
  );
}

const SuspenseWithLoader = (props: any) => {
  return (
    <Suspense fallback={<PageSpinner />}>
      <MemberContextProvider>
        <ManageMembers {...props} />
      </MemberContextProvider>
    </Suspense>
  );
};

export default SuspenseWithLoader;
